import { useState, useEffect, useCallback,useRef } from 'react';
import POExcelGrid from '@/components/POExcelGrid';
import { PurchaseOrder } from '@/lib/mockData';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { RefreshCw, Download, Info, Save, Users, Calendar, ShoppingCart, Package, FileText, Truck, LucideSquareUser } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { useSelector } from 'react-redux';
import { useLocation } from 'wouter';
import BASE_URL from '@/constants';
import { RootState } from '@/store';
import axios from 'axios';
import { useGridRole } from "./Custom-Hook-Role-Dashboard";
import { motion } from 'framer-motion';
import { ProgressiveLoader } from '@/components/ui/progressive-loader';

enum POstatuses {

  Completed = 'Completed',
  Pending = 'Pending',
  
}

type StageStatus = "pending" | "loading" | "success" | "error";
export enum UserRole {
  Manager = 'Manager',
  Planning = 'Planning Team',
  Appointment = 'Appointment Team',
  Sales_Order = 'Sales Order Team',
  Courier = 'Courier Team',
  Invoicing = 'Invoicing Team',
  Warehouse = 'Warehouse Team',
}

const roleIcons = {
  'Manager': <Users className="h-5 w-5 text-purple-600" />,
  'Planning Team': <Users className="h-5 w-5 text-blue-500" />,
  'Appointment Team': <Calendar className="h-5 w-5 text-green-500" />,
  'Sales Order Team': <ShoppingCart className="h-5 w-5 text-purple-500" />,
  'Warehouse Team': <Package className="h-5 w-5 text-teal-500" />,
  'Invoicing Team': <FileText className="h-5 w-5 text-indigo-500" />,
  'Courier Team': <Truck className="h-5 w-5 text-orange-500" />,
};

type AllowedRoles =
  | "Manager"
  | "Planning Team"
  | "Appointment Team"
  | "Sales Order Team"
  | "Courier Team"
  | "Invoicing Team"
  | "Warehouse Team";

const allowedRoles = [
  "Manager",
  "Planning Team",
  "Appointment Team",
  "Sales Order Team",
  "Courier Team",
  "Invoicing Team",
  "Warehouse Team"
] as const;

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);
};

// --- Fancy pulse animation helper for stat changes using state in parent ---
function getPulseStates(statsArr: any[], prevArr: any[] | null) {
  if (!prevArr) return statsArr.map(() => ({
    pending: false,
    value: false,
    completed: false
  }));
  return statsArr.map((item, idx) => {
    const prev = prevArr[idx] || {};
    return {
      pending: item.pending !== prev.pending,
      value: item.totalValue !== prev.totalValue,
      completed: item.completed !== prev.completed
    };
  });
}

export default function RoleDashboard() {
  const { toast } = useToast();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [filters, setFilters] = useState({ poNumber: '', location: '' });
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const role = useSelector((state: any) => state.user.role) || localStorage.getItem('userRole') || 'Manager';
  const username = useSelector((state: any) => state.user.name) || localStorage.getItem('userName') || '';
  const client = useSelector((state: any) => state.user.company_name) || localStorage.getItem('userClient') || '';
  const platform = useSelector((state: RootState) => state.platformOptions.selectedPlatform);

  const [gridRole, setGridRole] = useGridRole(role as AllowedRoles, allowedRoles);
  const isManager = role === "Manager";

  const [roleStats, setRoleStats] = useState<any>(null);
  const [pulseStates, setPulseStates] = useState<any[]>([]);
  const prevStatsRef = useRef<any[] | null>(null);

  const [refresh, setRefresh] = useState(false);


      // Define the stages with their initial status
    const initialStages = [
      { label: "Fetching POs from mail", status: "pending" as StageStatus },
      { label: "Syncing with latest data", status: "pending" as StageStatus },
      { label: "Aggregating dashboard", status: "pending" as StageStatus }
    ];
    
    // Create state for the stages
    const [stages, setStages] = useState(initialStages);

      const updateStage = (index: number, status: StageStatus) => {
      setStages(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], status };
        return updated;
      });
    };

  // --- Shimmer skeleton card ---
  const ShimmerCard = ({ idx }: { idx: number }) => (
    <motion.div
      className="w-full sm:w-1/2 md:w-1/3 bg-white rounded-2xl border border-gray-200 p-4 animate-pulse"
      style={{
        minWidth: 260,
        marginBottom: "1rem",
        background: "linear-gradient(270deg,#f3f0fd,#e9e5f7,#f3f0fd)",
        backgroundSize: "400% 400%",
        animation: "shimmer 1.3s ease-in-out infinite"
      }}
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: idx * 0.09 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 bg-gray-200 rounded-full" />
        <div className="h-5 w-20 bg-gray-200 rounded" />
      </div>
      <div className="h-4 w-10 bg-gray-200 rounded mb-2" />
      <div className="space-y-2 mt-3">
        <div className="h-3 w-28 bg-gray-200 rounded" />
        <div className="h-3 w-28 bg-gray-200 rounded" />
        <div className="h-3 w-28 bg-gray-200 rounded" />
      </div>
    </motion.div>
  );

  // --- Animated role card with glow, stat pulse, and icon wobble ---
  const renderRoleCard = (data: any, idx: number, pulse?: any) => (
    <motion.div
      key={data.team || data.role}
      className="w-full sm:w-1/2 md:w-1/4 bg-white rounded-2xl border border-gray-200 p-4 shadow-md relative group transition-all"
      initial={{ opacity: 0, y: 4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: idx * 0.05, type: "spring", stiffness: 80 }}
      whileHover={{ scale: 1.005, boxShadow: "0 0 0 3px #a78bfa22, 0 10px 32px rgba(124,58,237,0.12)" }}
      style={{ minWidth: 260, marginBottom: "1rem", position: "relative" }}
    >
      {/* Glow border */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        style={{ boxShadow: "0 0 0 0 #a78bfa", zIndex: 1 }}
        whileHover={{ boxShadow: "0 0 36px 6px #a78bfa66, 0 1.5px 24px 2px #8b5cf666" }}
        transition={{ type: "spring", duration: 0.01 }}
      />

      {/* Icon + title */}
      <motion.div
        className="flex items-center gap-2 mb-2 relative z-10"
        initial={{ scale: 1 }}
        animate={pulse?.icon ? { scale: [1, 1.2, 0.98, 1] } : { scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {roleIcons[data.role || data.team] || <LucideSquareUser className="h-5 w-5" />}
        <h3 className="font-semibold text-gray-900">{data.role || data.team}</h3>
      </motion.div>
      <div className="text-xs text-green-600 font-bold">{data.valueChange || '+0%'}</div>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between text-sm">
          <span>Pending</span>
          <motion.span
            animate={pulse?.pending ? { scale: [1, 1.18, 0.97, 1], color: "#a78bfa" } : { scale: 1, color: "#333" }}
            transition={{ duration: 0.47, type: "spring" }}
            className="font-semibold"
          >
            {data.pending || data.pendingPOs || 0}
          </motion.span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Total Value</span>
          <motion.span
            animate={pulse?.value ? { scale: [1, 1.14, 0.94, 1], color: "#f59e42" } : { scale: 1, color: "#f59e42" }}
            transition={{ duration: 0.49, type: "spring" }}
            className="font-semibold"
          >
            {formatCurrency(data.totalValue || 0)}
          </motion.span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Completed</span>
          <motion.span
            animate={pulse?.completed ? { scale: [1, 1.16, 0.93, 1], color: "#34d399" } : { scale: 1, color: "#333" }}
            transition={{ duration: 0.48, type: "spring" }}
            className="font-semibold"
          >
            {data.completed || data.completedPOs || 0}
          </motion.span>
        </div>
      </div>
      {/* Decorative animated dot */}
      <motion.span
        className="absolute right-3 bottom-3 h-2 w-2 rounded-full"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.4 + idx * 0.09, type: "spring" }}
        style={{ background: "linear-gradient(135deg, #a78bfa 60%, #fcd34d 100%)" }}
      />
    </motion.div>
  );

const handleRefresh = async () => {
  try {
    setRefresh(true);
    

    


    // Stage 1: Trigger Microservices
    updateStage(0, "loading");
    try {
      await Promise.all([
        axios.post("https://po-microservice-93503779962.asia-south2.run.app/scrape-blinkit-po", {
          platform: 0,
        }),
        axios.post("https://po-microservice-93503779962.asia-south2.run.app/scrape-zepto-po", {
          platform: 0,
        }),
      ]);
      updateStage(0, "success");
    } catch (error) {
      console.error("Error in stage 1:", error);
      updateStage(0, "error");
      throw error; // Re-throw to stop further execution
    }

    // Stage 2: Sync APIs
    updateStage(1, "loading");
    try {
      await Promise.all([
        axios.post(`${BASE_URL}/api/po-excel/sync-swiggy`),
        axios.post(`${BASE_URL}/api/po-excel/sync-blinkit`),
        axios.post(`${BASE_URL}/api/po-excel/sync-Zepto`),
      ]);
      updateStage(1, "success");
    } catch (error) {
      console.error("Error in stage 2:", error);
      updateStage(1, "error");
      throw error;
    }

    // Stage 3: Aggregate APIs
    updateStage(2, "loading");
    try {
      await Promise.all([
        axios.get(`${BASE_URL}/po-dashboard/brand-level`, {
          params: { client: client },
        }),
        axios.post(`${BASE_URL}/po-dashboard/aggregate`, {
          companyName: client,
        }),
      ]);
      updateStage(2, "success");
    } catch (error) {
      console.error("Error in stage 3:", error);
      updateStage(2, "error");
      throw error;
    }

 toast({ title: "Refresh Complete" });
     
  } catch (error) {
      toast({ title: "Error during refresh", description: error, variant: "destructive" });
     

    // alert("Something went wrong. Please check the console.");
  } finally {
     setStages(initialStages)
    setRefresh(false);
  }
};
  // --- Stat fetch and pulse update logic ---
  useEffect(() => {
    if (!client || !platform) return;
    setStatsLoading(true);
    async function fetchStats() {
      try {
        const res = await axios.get(`${BASE_URL}/po-dashboard/aggregate-by-team?client=${client}&platform=${platform}`);
        const data = res.data;
        if (gridRole === "Manager") {
          setRoleStats(data);
          setPulseStates(getPulseStates(data, prevStatsRef.current));
          prevStatsRef.current = data;
        } else {
          const found =
            data.find((item: any) =>
              (item.team || item.role || '').toLowerCase() === gridRole.toLowerCase()
            ) || null;
          setRoleStats(found);
          setPulseStates([getPulseStates([found], prevStatsRef.current)[0]]);
          prevStatsRef.current = [found];
        }
      } catch (err) {
        setRoleStats(null);
        setPulseStates([]);
      }
      setStatsLoading(false);
    }
    fetchStats();
    // eslint-disable-next-line
  }, [gridRole, client, platform]);

  useEffect(() => {
    if (!client || !platform) return;
    async function load() {
      setLoading(true);
      try {
        let url = `${BASE_URL}/api/po-excel/${client}/${platform}/paginated?page=${page}&limit=${pageSize}`;
        if (dateRange && dateRange.from && dateRange.to) {
          const startDate = dateRange.from instanceof Date ? dateRange.from.toISOString().slice(0, 10) : dateRange.from;
          const endDate = dateRange.to instanceof Date ? dateRange.to.toISOString().slice(0, 10) : dateRange.to;
          url += `&startDate=${startDate}&endDate=${endDate}`;
        }
        const response = await fetch(url);
        const { data, total } = await response.json();
        setPurchaseOrders(data);
        setTotalCount(total);
      } catch (err) {
        toast({ title: "Error", description: "Failed to load purchase orders.", variant: "destructive" });
        setPurchaseOrders([]);
        setTotalCount(0);
      }
      setLoading(false);
    }
    load();
  }, [client, platform, page, pageSize, toast, dateRange]);

  const dirtyRef = useRef(false);
  const changedPOIds = useRef<Set<string | number>>(new Set());

    const isTeamCompleted = useCallback((po: PurchaseOrder, team: string): boolean => {
    switch (team) {
      case 'planner':
        return (
          (po as any).sanityCheck === 'Complete' &&
          (po as any).inventoryCheck === 'Complete' &&
          !!(po as any).fcn &&
          !!(po as any).fillRate &&
          !!po.reasonCode 
        );
      case 'appointment':
        return (
          ((po as any).appointmentStatus === 'Complete' || (po as any).appointmentStatus === 'Confirmed') &&
          !!(po as any).physicalAppointmentDate &&
          !!(po as any).inwardDateRequested
        );
      case 'sales':
        return (
          (po as any).soProcessed === 'Yes' &&
          (po as any).soClosed === 'Yes' &&
          !!(po as any).soStatus &&
          !!(po as any).poSentToParty
        );
      case 'courier':
        return (
          (po as any).deliveryStatus === 'Delivered' &&
          !!(po as any).pickupDate &&
          !!(po as any).courierPickupFlag
        );
      case 'invoice':
        return (
          !!(po as any).invoiceNumber &&
          (po as any).sharedToWarehouse === 'Yes' &&
          !!(po as any).invoiceValue
        );
      case 'warehouse':
        return (
          (po as any).handoverToCourier === 'Complete' &&
          !!(po as any).picklistGeneration &&
          !!(po as any).pickAndPack &&
          !!(po as any).readyToDispatch
        );
      default:
        return false;
    }
  }, []);


  const handleUpdatePO = (id: number | string, updates: Partial<PurchaseOrder>) => {
    setPurchaseOrders(prev =>
      prev.map(po => {
        if (po.id !== id) return po;
        const updatedPO = { ...po, ...updates, updatedAt: new Date() };

        // Team completion logic
        const teamStatusMap = [
          { team: 'planner', field: 'PlannerStatus' },
          { team: 'appointment', field: 'AppointmentStatus' },
          { team: 'sales', field: 'SalesOrderStatus' },
          { team: 'courier', field: 'CourierStatus' },
          { team: 'invoice', field: 'InvoiceStatus' },
          { team: 'warehouse', field: 'WarehouseStatus' },
        ];

        let allCompleted = true;
        for (const { team, field } of teamStatusMap) {
          if (isTeamCompleted(updatedPO, team)) {
            updatedPO[field] = POstatuses.Completed;
          } else {
            updatedPO[field] = POstatuses.Pending;
            allCompleted = false;
          }
        }

        // Optionally, if all teams are completed, set all status fields to 'Completed'
        if (allCompleted) {
          for (const { field } of teamStatusMap) {
            updatedPO[field] = POstatuses.Completed;
          }
        }

        return updatedPO;
      })
    );
    dirtyRef.current = true;
    changedPOIds.current.add(id);
  };

  const handleBatchSave = async () => {
    if (!dirtyRef.current || changedPOIds.current.size === 0) {
      toast({ title: "No changes", description: "No purchase orders have been modified." });
      return;
    }
    const payload = purchaseOrders
      .filter(po => changedPOIds.current.has(po.id))
      .map(po => ({
        ...po,
        client,
        platform,
      }));
    if (payload.length === 0) {
      toast({ title: "No changes", description: "No purchase orders have been modified." });
      return;
    }

    let requestBody = { purchaseOrders: payload };
    const res = await fetch(`${BASE_URL}/api/po-excel/upsert-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    if (!res.ok) {
      toast({ title: "Save Failed", description: "Failed to save purchase orders.", variant: "destructive" });
      return;
    }
    dirtyRef.current = false;
    const client_name = client;
    //  const now = Date.now();

    for (const po of payload) {
      await fetch(BASE_URL + '/api/audit-log/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          PO_ID: po.id,
          client_name,
          username,
          role,
          action: "updated",
          platform,
          poNumber: po.poNumber,
          // timestamp: now,
        }),
      });
    }
    changedPOIds.current.clear();

    toast({
      title: "All Changes Saved",
      description: `Purchase orders for ${client} on ${platform} saved and audit logged.`,
    });

    // --- Refetch role stats in background ---
    try {
      const statsRes = await axios.get(`${BASE_URL}/po-dashboard/aggregate-by-team?client=${client}&platform=${platform}`);
      const data = statsRes.data;
      if (gridRole === "Manager") {
        setRoleStats(data);
        setPulseStates(getPulseStates(data, prevStatsRef.current));
        prevStatsRef.current = data;
      } else {
        const found =
          data.find((item: any) =>
            (item.team || item.role || '').toLowerCase() === gridRole.toLowerCase()
          ) || null;
        setRoleStats(found);
        setPulseStates([getPulseStates([found], prevStatsRef.current)[0]]);
        prevStatsRef.current = [found];
      }
    } catch (err) {
      // Optionally handle error
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['PO Number', 'Brand', 'Location', 'Status', 'Amount'].join(','),
      ...purchaseOrders.map(po => [
        po.poNumber,
        po.brand,
        po.location,
        po.status,
        po.totalAmount
      ].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'purchase_orders.csv';
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: "Purchase order data exported to CSV",
    });
  };

  
  
  // --- Main Render ---
  return (
    <div className="space-y-6 p-6 relative">
      {
        refresh &&     <ProgressiveLoader stages={stages} />
      }
    
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">PO Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Role-based editing with real-time validation
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <DateRangeFilter onDateRangeChange={setDateRange} />
          <Dialog>
            <DialogTrigger asChild>
              <motion.button
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.96 }}
                className="flex items-center gap-2 border border-gray-300 rounded px-4 py-2 bg-white"
              >
                <Info className="h-4 w-4" />
                Help
              </motion.button>
            </DialogTrigger>
            <DialogContent className="max-w-sm bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
              <DialogHeader className="pb-2">
                <DialogTitle className="flex items-center gap-2 text-lg font-bold text-purple-800">
                  <Info className="h-5 w-5 text-purple-600" />
                  Excel-like Features
                </DialogTitle>
              </DialogHeader>
            </DialogContent>
          </Dialog>
          <motion.button
            whileHover={{ scale: 1.08,  }}
            whileTap={{ scale: 0.98 }}
            onClick={handleRefresh}
            // onClick={() => toast({ title: "Under Development", description: "This feature is under development." })}
            className="flex items-center gap-2 border border-gray-300 rounded px-4 py-2 bg-white"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.09,  }}
            whileTap={{ scale: 0.97 }}
            onClick={handleExport}
            className="flex items-center gap-2 border border-gray-300 rounded px-4 py-2 bg-white"
          >
            <Download className="h-4 w-4" />
            Export
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.08, }}
            whileTap={{ scale: 0.97 }}
            onClick={handleBatchSave}
            className="flex items-center gap-2 border border-purple-500 bg-purple-600 text-white px-4 py-2 rounded font-semibold"
          >
            <Save className="h-4 w-4" />
            Save All
          </motion.button>
        </div>
      </div>

      {/* --- New role stats/cards section --- */}
      <h2 className="text-lg font-semibold mb-2">Role PO Stats</h2>
      <div className="flex flex-wrap gap-6">
        {statsLoading
          ? Array.from({ length: 6 }).map((_, idx) => <ShimmerCard key={idx} idx={idx} />)
          : gridRole === "Manager"
            ? Array.isArray(roleStats) && roleStats.length > 0
              ? roleStats.map((stat: any, idx: number) => renderRoleCard(stat, idx, pulseStates[idx] || {}))
              : <p className="text-sm text-gray-500">No data available for this role.</p>
            : roleStats
              ? renderRoleCard(roleStats, 0, pulseStates[0])
              : <p className="text-sm text-gray-500">No data available for this role.</p>
        }
      </div>

      {/* Team Dropdown For Manager */}
      {/* {isManager && (
        <div className="flex items-center gap-2 ml-2">
          <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Role:</span>
          <select
            className="flex-1 px-2 py-2 bg-white border border-purple-200 rounded-md text-sm"
            value={gridRole}
            onChange={e => setGridRole(e.target.value as AllowedRoles)}
            style={{ minWidth: 160 }}
          >
            {allowedRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>
      )} */}

      {/* --- Filters --- */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">PO Number:</span>
            <input
              type="text"
              placeholder="Search PO..."
              value={filters.poNumber}
              onChange={(e) => setFilters(prev => ({ ...prev, poNumber: e.target.value }))}
              className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Location:</span>
            <input
              type="text"
              placeholder="Search Location..."
              value={filters.location}
              onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
              className="flex-1 px-3 py-2 bg-white border border-purple-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
          <span>
            {role === "Manager" ? 'Full access to all fields' : `Can edit: ${role} fields`}
          </span>
          <span>Showing {purchaseOrders.filter(po =>
            (!filters.poNumber || po.poNumber.toLowerCase().includes(filters.poNumber.toLowerCase())) &&
            (!filters.location || po.location.toLowerCase().includes(filters.location.toLowerCase()))
          ).length} of {purchaseOrders.length} POs</span>
        </div>
      </div>

      {/* --- Excel Grid or Loader --- */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <span className="text-purple-600 text-lg font-semibold animate-pulse">Loading purchase orders...</span>
        </div>
      ) : (
        <POExcelGrid
          purchaseOrders={purchaseOrders}
          currentUser={{
            id: Number(localStorage.getItem("userId")) || 1,
            username,
            name: username,
            role: gridRole,
          }}
          onUpdatePO={handleUpdatePO}
          filters={filters}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
}
